import { api } from '@/plugins/api'

const REPORT_ENDPOINT = '/dashboard/client-errors'
const THROTTLE_MS = 5000
const MAX_QUEUE = 20

let lastReportedAt = 0
let queue: Array<Record<string, unknown>> = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function flush() {
  if (queue.length === 0) return
  const batch = queue.splice(0, MAX_QUEUE)
  lastReportedAt = Date.now()

  for (const entry of batch) {
    api.post(REPORT_ENDPOINT, entry).catch(() => {
      // Silently ignore reporting failures to avoid infinite loops
    })
  }
}

function enqueue(entry: Record<string, unknown>) {
  // Dedup by message: skip if identical message already queued
  const msg = String(entry.message || '')
  if (queue.some(e => String(e.message || '') === msg)) {
    return
  }

  queue.push(entry)
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE)
  }

  // Throttle flush: batch errors within 1s, but no more often than THROTTLE_MS
  if (!flushTimer) {
    const delay = Math.max(1000, THROTTLE_MS - (Date.now() - lastReportedAt))
    flushTimer = setTimeout(() => {
      flushTimer = null
      flush()
    }, delay)
  }
}

/**
 * Диагностическое сообщение в журнал клиентских ошибок дашборда
 * (не исключение, а след проблемы — например, неудачное обновление PWA).
 */
export function reportClientDiagnostic(type: string, message: string) {
  enqueue({ type, message: message.substring(0, 450), url: window.location.href })
}

/**
 * Install global error handlers for the Vue app.
 * Call once in main.ts after app creation.
 */
export function installErrorReporter(app: { config: { errorHandler?: unknown } }) {
  // 1. Vue component errors
  app.config.errorHandler = (err: unknown, _instance: unknown, info: string) => {
    const error = err instanceof Error ? err : new Error(String(err))
    console.error('[Vue Error]', info, error)

    enqueue({
      type: 'vue',
      message: error.message,
      source: info,
      stack: error.stack?.substring(0, 1000),
      url: window.location.href,
    })
  }

  // 2. Uncaught JS errors
  window.addEventListener('error', (event) => {
    // Ignore script loading errors (CORS, ad-blockers)
    if (!event.message) return

    enqueue({
      type: 'js',
      message: event.message,
      source: event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : undefined,
      url: window.location.href,
    })
  })

  // 3. Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      reason instanceof Error ? reason.message : String(reason || 'Unknown rejection')

    enqueue({
      type: 'promise',
      message,
      stack: reason instanceof Error ? reason.stack?.substring(0, 1000) : undefined,
      url: window.location.href,
    })
  })
}
